import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import Link from 'next/link'

const About = () => {
  return (
    <>
      <Grid container spacing={6}>
        <Grid size={{ xs: 12 }}>
          <Card
            sx={{
              padding: '2rem',
              borderRadius: '12px',
              boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
              borderTop: '6px solid',
              borderTopColor: 'primary.main'
            }}
          >
            <Typography variant='h4' component='h1' color='primary' gutterBottom>
              About WAO Tools
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Box sx={{ maxWidth: 800 }}>
              <Typography variant='h6' component='h2' color='primary' gutterBottom>
                What is WAO Tools?
              </Typography>
              <Typography variant='body1' paragraph>
                WAO Tools is a free, independent web application built specifically for players of{' '}
                <strong>War and Order</strong> — the popular mobile strategy game. The site collects calculators,
                planners, and reference tools that help players make better decisions, plan their growth, and understand
                the game more deeply, all in one place.
              </Typography>
              <Typography variant='body1' paragraph>
                From troop formation builders and artifact calculators to stamina planners and building progression
                guides, WAO Tools exists to make your journey through the game less painful and more informed. Whether
                you are a new player trying to understand where to invest your resources, or a seasoned lord optimizing
                every detail of your castle, there is something here for you.
              </Typography>

              <Divider sx={{ my: 3 }} />

              <Typography variant='h6' component='h2' color='primary' gutterBottom>
                The Story
              </Typography>
              <Typography variant='body1' paragraph>
                WAO Tools was created in <strong>2023</strong> by <strong>oNare</strong>, a War and Order player who got
                tired of juggling spreadsheets, Discord posts, and half-remembered advice to figure out the most basic
                game decisions. The kind of questions like: "How many speed-ups do I need to finish this building?" or
                "Which artifacts work best for my playstyle?" had no fast, reliable answer in one place.
              </Typography>
              <Typography variant='body1' paragraph>
                The solution was to build it. WAO Tools started as a personal project — a small set of calculators to
                answer the questions that kept coming up. Over time, more tools were added, the community responded with
                feedback and suggestions, and the site grew into what it is today: a comprehensive toolkit covering
                artifacts, troops, buildings, beasts, equipment, mystical college, and more.
              </Typography>
              <Typography variant='body1' paragraph>
                The goal has always been the same: give War and Order players the ability to plan, compare, and
                strategize with real information — so you can spend less time guessing and more time building a castle
                that actually dominates.
              </Typography>

              <Divider sx={{ my: 3 }} />

              <Typography variant='h6' component='h2' color='primary' gutterBottom>
                What Makes It Different
              </Typography>
              <Typography variant='body1' paragraph>
                WAO Tools is made by a player, for players. Every tool on this site was built to answer a real in-game
                need. There are no paywalls, no account requirements, no advertisements that obscure the content. The
                tools run in your browser and your data stays on your device — nothing is sent to a server.
              </Typography>
              <Typography variant='body1' paragraph>
                The site is also community-driven. If you find an error in the data, have a suggestion for a new tool,
                or want to contribute game knowledge, the best way to reach out is through the{' '}
                <Link href='/contact/' style={{ color: 'inherit' }}>
                  contact page
                </Link>
                . WAO Tools is a living project — it improves because players like you point out what needs to improve.
              </Typography>

              <Divider sx={{ my: 3 }} />

              {/*
                Esta seccion existe para que la explicacion de donde sale el dato
                viva en un solo lugar. Antes se servia como respuesta de FAQ en 12
                paginas de herramienta —528 palabras identicas— y la guia de AdSense
                pide lo contrario: un resumen breve en cada pagina y un enlace al
                detalle. El id='data' es el destino de ese enlace.
              */}
              <Typography variant='h6' component='h2' color='primary' gutterBottom id='data'>
                Where the Game Data Comes From
              </Typography>
              <Typography variant='body1' paragraph>
                War and Order exposes no public API, so none of the numbers on this site are read from the game or from
                your account. Every table here was typed in by players and checked against what the game actually shows:
                the {'440'} building upgrade steps, the artifacts and their stats per star, the equipment materials, the
                realm merge history, the Obsidian and VIP tables. When a patch changes a cost, someone has to notice and
                correct it by hand.
              </Typography>
              <Typography variant='body1' paragraph>
                That has two honest consequences. The data can lag a patch, and a figure can be wrong. If a number does
                not match what you see in your own castle, the{' '}
                <Link href='/contact/' style={{ color: 'inherit' }}>
                  contact page
                </Link>{' '}
                is where that gets fixed — several tables on this site were corrected because a player reported a
                mismatch. Costs shown are base costs: construction buffs, alliance help and event discounts all apply on
                top and vary per account, so folding them in would make the tables wrong for everyone.
              </Typography>

              <Divider sx={{ my: 3 }} />

              <Typography variant='h6' component='h2' color='primary' gutterBottom>
                Disclaimer
              </Typography>
              <Typography variant='body2' color='text.secondary' paragraph>
                WAO Tools is an independent, fan-made project and is not affiliated with, endorsed by, or sponsored by
                the publishers or developers of War and Order. All game names, trademarks, and game assets remain the
                property of their respective owners. See our full{' '}
                <Link href='/disclaimer/' style={{ color: 'inherit' }}>
                  disclaimer
                </Link>
                .
              </Typography>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </>
  )
}

About.noLoadingGate = true

export default About
